import { Component, createRef } from 'react';
import { connect } from 'react-redux';
import emptyFunction from 'react-utils/emptyFunction';
import styled from 'foundations-theming/utils';
import withTheme from 'ContentComponents/components/cmv2/decorators/withTheme';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { makeGetHasValidationError } from 'ContentData/selectors/validationErrors';
import ErrorIcon from 'ContentComponents/components/cmv2/ErrorIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Box } from 'foundations-components/transitional/components/Box';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { Text } from 'foundations-components/components/Text';
import { H5 } from 'foundations-components/transitional/components/H5';
import { H6 } from 'foundations-components/transitional/components/H6';
import { SpacedDiv } from 'ContentComponents/components/cmv2/components/SpacedComponent';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Alert } from 'foundations-components/transitional/components/Alert';
import EditFormLabel from 'ContentComponents/components/cmv2/EditFormLabel';
import SanitizedLabel from 'ContentComponents/components/cmv2/SanitizedLabel';
import ExpandableSection from 'ContentComponents/components/cmv2/components/ExpandableSection';
import { FieldGroupWrapper } from 'ContentComponents/components/cmv2/components/FieldGroupWrapper';
import { GROUP } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import FieldLabelIcon from '../FieldLabelIcon';
import { DISPLAY_OPTIONS } from '../constants';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { SideNavItem } from 'foundations-components/transitional/components/SideNav';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LabelWrapper = styled.span.withConfig({
  displayName: "FieldGroup__LabelWrapper"
})(["cursor:pointer;"]);

// Wrap help icon in div that matches height so that correct alignment happens
// Otherwise wrapper will match label height and icon is not aligned in Flex properly
const HelpIconWrapper = styled.div.withConfig({
  displayName: "FieldGroup__HelpIconWrapper"
})(["height:18px;"]);
const StyledUl = styled.ul.withConfig({
  displayName: "FieldGroup__StyledUl"
})(({
  theme
}) => {
  return {
    listStyle: 'none',
    marginBlock: theme.space['0'],
    paddingInlineStart: theme.space['300'],
    marginInline: theme.space['600']
  };
});
const makeMapStateToProps = () => {
  const getHasValidationError = makeGetHasValidationError();
  return (state, props) => ({
    hasValidationError: getHasValidationError(state, props)
  });
};
const connector = connect(makeMapStateToProps);
class FieldGroup extends Component {
  constructor(props) {
    super(props);
    this.handleFieldGroupOpenChange = this.handleFieldGroupOpenChange.bind(this);
    this.handleTabChange = this.handleTabChange.bind(this);
    this.helpTextRef = /*#__PURE__*/createRef();
    this.state = {
      isLabelTruncated: false,
      helpTextWidth: 0
    };
    this.resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const {
          width
        } = entry.contentRect;
        this.setState({
          helpTextWidth: width
        });
      }
    });
  }
  handleTabChange(tabId) {
    this.setState({
      selectedTabId: tabId
    });
  }
  get fieldKey() {
    return this.props.fieldKey.join('.');
  }
  componentDidMount() {
    var _this$helpTextRef$cur;
    this.setState({
      helpTextWidth: (_this$helpTextRef$cur = this.helpTextRef.current) === null || _this$helpTextRef$cur === void 0 ? void 0 : _this$helpTextRef$cur.getBoundingClientRect().width
    });
    if (this.helpTextRef.current) {
      this.resizeObserver.observe(this.helpTextRef.current);
    }
  }
  componentWillUnmount() {
    this.resizeObserver.disconnect();
  }
  handleFieldGroupOpenChange(evt) {
    const {
      onFieldGroupOpenChange
    } = this.props;
    onFieldGroupOpenChange(evt.target.value, this.fieldKey);
  }
  renderHelpTextTooltip() {
    const {
      help_text: helpText
    } = this.props;
    if (!helpText) return null;
    return /*#__PURE__*/_jsx(HelpIconWrapper, {
      children: /*#__PURE__*/_jsx(HelpIcon, {
        paddingBlock: "0",
        paddingInlineEnd: "100",
        paddingInlineStart: "100",
        size: "300",
        color: "icon-core-subtle",
        "data-selenium": `${this.fieldKey}:helpText`,
        "aria-label": helpText,
        title: /*#__PURE__*/_jsx(SanitizedLabel, {
          label: helpText
        })
      })
    });
  }
  renderErrorTooltip() {
    const {
      hasValidationError
    } = this.props;
    if (!hasValidationError) return null;
    return /*#__PURE__*/_jsx(Tooltip, {
      placement: "top right",
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ContentComponents.cmv2.errorIcon.groupEditMessage"
      }),
      children: /*#__PURE__*/_jsx(View, {
        as: "div",
        children: /*#__PURE__*/_jsx(ErrorIcon, {
          "data-selenium": `${this.fieldKey}:validationError`
        })
      })
    });
  }

  // Returns fixed children for a group label
  renderFixedChildren() {
    const {
      GroupLabelChild,
      fieldKey,
      isDefaultValue
    } = this.props;
    if (!GroupLabelChild) return null;
    return /*#__PURE__*/_jsx(GroupLabelChild, {
      fieldKey: fieldKey,
      isDefaultValue: isDefaultValue
    });
  }
  renderLabel() {
    const {
      inherited_value: inheritedValue,
      isDefaultValue,
      label,
      LabelComponent,
      fieldKey,
      allowFieldReset,
      onFieldReset,
      onFieldUnlink,
      scrollToField
    } = this.props;
    return /*#__PURE__*/_jsx(EditFormLabel, {
      fieldKey: fieldKey,
      fieldType: GROUP,
      inheritedValue: inheritedValue,
      isDefaultValue: isDefaultValue,
      onFieldReset: onFieldReset,
      onFieldUnlink: onFieldUnlink,
      label: label,
      LabelComponent: LabelComponent,
      allowFieldReset: allowFieldReset,
      scrollToField: scrollToField,
      truncate: true
    });
  }
  renderLabelAndHelpText(isInline) {
    const {
      icon
    } = this.props;
    const label = this.renderLabel();
    const TextComponent = isInline ? H5 : H6;
    const {
      helpTextWidth
    } = this.state;
    return /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      marginBlock: isInline ? '200' : undefined,
      paddingInline: isInline ? '600' : undefined,
      children: [icon && icon.name && /*#__PURE__*/_jsx(Box, {
        grow: 0,
        children: /*#__PURE__*/_jsx(FieldLabelIcon, {
          icon: icon
        })
      }), /*#__PURE__*/_jsx(TextComponent, {
        margin: "0",
        maxInlineSize: helpTextWidth ? `calc(100% - ${helpTextWidth}px)` : '100%',
        textTransform: "none",
        children: isInline ? label : /*#__PURE__*/_jsx(LabelWrapper, {
          children: label
        })
      }), /*#__PURE__*/_jsx(Box, {
        grow: 0,
        shrink: 0,
        ref: this.helpTextRef,
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          children: [this.renderFixedChildren(), this.renderHelpTextTooltip()]
        })
      })]
    });
  }
  renderDrilldown() {
    const {
      onEditGroupClick = emptyFunction,
      icon,
      isInGlobalStyles
    } = this.props;
    if (isInGlobalStyles) {
      return /*#__PURE__*/_jsx(StyledUl, {
        children: /*#__PURE__*/_jsx(SideNavItem, {
          iconLeft: icon && icon.name && /*#__PURE__*/_jsx(FieldLabelIcon, {
            icon: icon
          }),
          title: this.renderLabel(),
          onClick: onEditGroupClick,
          iconRight: /*#__PURE__*/_jsx(RightIcon, {}),
          paddingBlock: "200"
        })
      });
    }
    return /*#__PURE__*/_jsx(FieldGroupWrapper, {
      "data-field-key": this.fieldKey,
      "data-test-id": "cmv2-field-group-drilldown",
      highlightOnHover: true,
      onClick: onEditGroupClick,
      paddingInline: "600",
      paddingBlock: "400",
      children: /*#__PURE__*/_jsx(MediaObject, {
        align: "center",
        "data-test-id": "cmv2-field-group-drilldown-content",
        itemLeft: this.renderErrorTooltip(),
        itemRight: /*#__PURE__*/_jsx(RightIcon, {
          color: "icon-interactive-default",
          "data-test-id": "cmv2-field-group-drilldown-caret",
          alignWithText: true,
          size: "200"
        }),
        children: this.renderLabelAndHelpText()
      })
    });
  }
  renderAccordion() {
    const {
      children,
      disabled,
      disabledMessage,
      fieldGroupOpen = false,
      inline_help_text: inlineHelpText,
      stretchToSides,
      useOnDark,
      withContentPadding
    } = this.props;
    return /*#__PURE__*/_jsxs(ExpandableSection, {
      "data-field-key": this.fieldKey,
      onOpenChange: this.handleFieldGroupOpenChange,
      open: fieldGroupOpen,
      stretchToSides: stretchToSides,
      useOnDark: useOnDark,
      titleText: /*#__PURE__*/_jsx(MediaObject, {
        align: "center",
        itemLeft: this.renderErrorTooltip(),
        children: this.renderLabelAndHelpText()
      }),
      withContentPadding: withContentPadding,
      "data-test-id": "cmv2-field-group-accordion",
      children: [inlineHelpText && /*#__PURE__*/_jsx(SpacedDiv, {
        marginBlockStart: "400",
        paddingInline: "600",
        children: /*#__PURE__*/_jsx(Text, {
          as: "small",
          display: "block",
          use: "help",
          children: /*#__PURE__*/_jsx("strong", {
            children: /*#__PURE__*/_jsx(SanitizedLabel, {
              label: inlineHelpText,
              useStrict: true
            })
          })
        })
      }), disabled && disabledMessage && /*#__PURE__*/_jsx(Alert, {
        type: "tip",
        marginInline: "600",
        marginBlock: "300",
        children: /*#__PURE__*/_jsx(SanitizedLabel, {
          label: disabledMessage,
          useStrict: true
        })
      }), children]
    });
  }
  renderInlineGroup() {
    const {
      children,
      firstChildIsGroup,
      hideLabel,
      inline_help_text: inlineHelpText
    } = this.props;
    return /*#__PURE__*/_jsxs(SpacedDiv, {
      marginBlock: "600",
      paddingBlockEnd: "500",
      "data-test-id": "cmv2-field-group-inline",
      "data-field-key": this.fieldKey,
      "data-is-group": true,
      children: [!hideLabel && this.renderLabelAndHelpText(true), !hideLabel && inlineHelpText && /*#__PURE__*/_jsx(SpacedDiv, {
        marginBlockStart: "300",
        marginBlockEnd: firstChildIsGroup ? '300' : '100',
        paddingInline: "600",
        children: /*#__PURE__*/_jsx(Text, {
          as: "small",
          display: "block",
          use: "help",
          children: /*#__PURE__*/_jsx("strong", {
            children: /*#__PURE__*/_jsx(SanitizedLabel, {
              label: inlineHelpText,
              useStrict: true
            })
          })
        })
      }), children]
    });
  }
  renderTabs() {
    var _;
    const {
      children,
      childFields,
      hideLabel,
      inline_help_text: inlineHelpText
    } = this.props;
    const childrenArray = children || [];
    const defaultTabId = (_ = childFields[0]) === null || _ === void 0 ? void 0 : _.name;
    const selectedTabId = this.state.selectedTabId || defaultTabId;
    const selectedIndex = childFields.findIndex(field => field.name === selectedTabId);
    const selectedContent = selectedIndex >= 0 ? childrenArray[selectedIndex] : childrenArray[0];
    return /*#__PURE__*/_jsxs(SpacedDiv, {
      marginBlock: "600",
      paddingBlockEnd: "500",
      "data-test-id": "cmv2-field-group-tabs",
      "data-field-key": this.fieldKey,
      "data-is-group": true,
      children: [!hideLabel && this.renderLabelAndHelpText(true), !hideLabel && inlineHelpText && /*#__PURE__*/_jsx(SpacedDiv, {
        marginBlock: "300",
        paddingInline: "600",
        children: /*#__PURE__*/_jsx(Text, {
          as: "small",
          display: "block",
          use: "help",
          children: /*#__PURE__*/_jsx("strong", {
            children: /*#__PURE__*/_jsx(SanitizedLabel, {
              label: inlineHelpText,
              useStrict: true
            })
          })
        })
      }), /*#__PURE__*/_jsxs(View, {
        borderColor: "border-core-subtle",
        borderRadius: "300",
        borderStyle: "solid",
        borderWidth: "100",
        marginInline: "300",
        children: [/*#__PURE__*/_jsx(Flex, {
          paddingBlockStart: "400",
          paddingInline: "400",
          gap: "0",
          children: childFields.map((fieldModel, index) => {
            const tabId = fieldModel.name;
            const tabTitle = fieldModel.label || tabId;
            const isActive = tabId === selectedTabId;
            const borderStartRadius = index === 0 ? undefined : '0';
            const borderEndRadius = index === childFields.length - 1 ? undefined : '0';
            return /*#__PURE__*/_jsx(Button, {
              use: "tertiary-light",
              active: isActive,
              onClick: () => this.handleTabChange(tabId),
              size: "sm",
              marginInline: "0",
              borderStartStartRadius: borderStartRadius,
              borderEndStartRadius: borderStartRadius,
              borderStartEndRadius: borderEndRadius,
              borderEndEndRadius: borderEndRadius,
              children: tabTitle
            }, tabId);
          })
        }), /*#__PURE__*/_jsx(Box, {
          paddingBlockEnd: "400",
          children: selectedContent
        })]
      })]
    });
  }
  render() {
    const {
      containsNestedGroups = false,
      display
    } = this.props;

    // if group display type is explicitly set, render it
    switch (display) {
      case DISPLAY_OPTIONS.DRILLDOWN:
        return this.renderDrilldown();
      case DISPLAY_OPTIONS.ACCORDION:
        return this.renderAccordion();
      case DISPLAY_OPTIONS.INLINE:
        return this.renderInlineGroup();
      case DISPLAY_OPTIONS.TABS:
        return this.renderTabs();
      default:
        // otherwise fall back on nested groups to determine view
        return containsNestedGroups ? this.renderDrilldown() : this.renderAccordion();
    }
  }
}
export default withTheme(connector(FieldGroup));